import { Formik, Field, Form } from "formik";
import { FC, useState, useEffect, useMemo, useRef } from "react";
import {
    User,
    TypeAction,
    UserRoles,
    StateActionPermission,
} from "../../../models/models";
import {
    useUpdateUserMutation,
    useCreateUserMutation,
    useGetProductsListQuery,
    useGetProjectsListQuery,
    useGetAvailablePermissionsQuery,
} from "../../../redux/RTKQuery/api";
import ActionButtons from "../../../assets/components/ActionButtons/ActionButtons";
import "./UsersSubRow.scss";
import TextInput from "../../../assets/components/Inputs/TextInput/TextInput";
import * as Yup from "yup";
import Select from "../../../assets/components/Select/Select";
import SearchBarMultiSelect from "../../../assets/components/SearchBar/SearchBarMultiSelect/SearchBarMultiSelect";
import { OptionList } from "../../../assets/helpers/helpers";
import {
    buildPermissionOptions,
    permissionIdsToKeys,
    permissionKeysToIds,
} from "../userPermissions";
import { sha256 } from "js-sha256";
import { useNotification } from "../../../assets/hooks/useNotification";
import { actionText } from "../../../text";

const OPTIONS_PAGE_SIZE = 1000;

type formField = {
    id?: number;
    username: string;
    password?: any;
};

export interface EditRowUser extends User {
    closeFormHandler: (id: number) => void;
    userRoles?: UserRoles | undefined;
}

const UsersSubRow: FC<EditRowUser> = ({
    id,
    username,
    role,
    permissions: userPermissions = [],
    productIds: userProductIds = [],
    projectIds: userProjectIds = [],
    closeFormHandler,
}) => {
    const [updateUser] = useUpdateUserMutation({
        fixedCacheKey: "updated_user",
    });
    const [createUser] = useCreateUserMutation({
        fixedCacheKey: "created_user",
    });
    const [typeAction, setTypeAction] = useState<TypeAction>({ type: "create" });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [disablePasswordField, setDisablePasswordField] = useState(false);
    const { ConfirmationMessageBox } = useNotification();

    const { data: productsData } = useGetProductsListQuery({
        pageNumber: 1,
        pageSize: OPTIONS_PAGE_SIZE,
    });
    const { data: projectsData } = useGetProjectsListQuery({
        pageNumber: 1,
        pageSize: OPTIONS_PAGE_SIZE,
    });
    const { data: permissionsData } = useGetAvailablePermissionsQuery();

    const productOptions: OptionList = useMemo(
        () => (productsData?.products ?? []).map(({ id, name }) => ({ id, name })),
        [productsData]
    );
    const projectOptions: OptionList = useMemo(
        () => (projectsData?.projects ?? []).map(({ id, name }) => ({ id, name })),
        [projectsData]
    );
    const permissionOptions: OptionList = useMemo(
        () => buildPermissionOptions(permissionsData?.labels ?? {}),
        [permissionsData]
    );
    const roleOptions: UserRoles = useMemo(
        () =>
            (permissionsData?.roles ?? []).map((roleItem, index) => ({
                id: index,
                name: roleItem.name ?? "",
            })),
        [permissionsData]
    );

    const [userRoleId, setUserRoleId] = useState<number>(0);
    const [selectedProductIds, setSelectedProductIds] =
        useState<number[]>(userProductIds);
    const [selectedProjectIds, setSelectedProjectIds] =
        useState<number[]>(userProjectIds);
    const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);

    const rolePermissionKeys = (roleName: string): string[] =>
        permissionsData?.roles?.find((roleItem) => roleItem.name === roleName)
            ?.permissions ?? [];

    const initializedRef = useRef(false);
    useEffect(() => {
        if (initializedRef.current || !roleOptions.length) return;

        const matched = roleOptions.find((roleItem) => roleItem.name === role);
        const initialRole = matched ?? roleOptions[0];
        setUserRoleId(initialRole.id);

        setSelectedPermissionIds(
            id === -1
                ? permissionKeysToIds(
                      permissionOptions,
                      rolePermissionKeys(initialRole.name)
                  )
                : permissionKeysToIds(permissionOptions, userPermissions)
        );

        initializedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleOptions, permissionOptions]);

    const toggleTypePassword = () => setShowPassword(!showPassword);
    const toggleTypeConfirmPassword = () =>
        setShowConfirmPassword(!showConfirmPassword);
    const disablePasswordFieldButton = () =>
        setDisablePasswordField(!disablePasswordField);

    useEffect(() => {
        id === -1
            ? setTypeAction({ type: "create" })
            : setTypeAction({ type: "update" });
        setDisablePasswordField(id === -1 ? false : true);
    }, [id]);

    const onChangeRoleHandler = (currentId: number): void => {
        setUserRoleId(currentId);
        const roleName =
            roleOptions.find((roleItem) => roleItem.id === currentId)?.name ?? "";
        setSelectedPermissionIds(
            permissionKeysToIds(permissionOptions, rolePermissionKeys(roleName))
        );
    };

    // Password is only editable when creating a user or after the "Change"
    // button unlocks the field on an existing user. When it stays locked we
    // must NOT send the password, otherwise the backend would overwrite it.
    const isPasswordEditable = typeAction.type === "create" || !disablePasswordField;

    const submitHandler = async (values: formField) => {
        const roleName =
            roleOptions.find((roleItem) => roleItem.id === userRoleId)?.name ?? "";
        const permissions = permissionIdsToKeys(
            permissionOptions,
            selectedPermissionIds
        );

        const dataForExport: Partial<User> = {
            id: values.id,
            username: values.username,
            role: roleName,
            permissions,
            productIds: selectedProductIds,
            projectIds: selectedProjectIds,
        };

        if (isPasswordEditable) {
            dataForExport.password = sha256(values.password);
        }

        ConfirmationMessageBox({
            textContent: actionText(typeAction.type as StateActionPermission, "user"),
        }).then((value) => {
            if (value !== "yes") return;

            if (typeAction.type === "create") {
                delete dataForExport.id;
                createUser(dataForExport);
            } else if (typeAction.type === "update") {
                updateUser(dataForExport);
            }
        });
    };

    const validateSchema = Yup.object().shape({
        username: Yup.string()
            .required("Required")
            .matches(/^(\S+$)/, " This field cannot contain only blankspaces"),
        password: isPasswordEditable
            ? Yup.string()
                  .required("Required")
                  .min(5, "Your password is too short.")
                  .matches(
                      /[a-zA-Z]/,
                      "Password must contain at least one Latin letter."
                  )
            : Yup.string().notRequired(),
        repeat: isPasswordEditable
            ? Yup.string()
                  .required("Required")
                  .oneOf([Yup.ref("password")], "Passwords must match")
            : Yup.string().notRequired(),
    });

    return (
        <Formik
            initialValues={{
                id,
                username,
                // Never seed the password with the stored hash coming from the
                // users list — otherwise unlocking "Change" and saving without
                // retyping would re-hash the old hash and corrupt the password.
                password: "",
                repeat: "",
            }}
            validationSchema={validateSchema}
            onSubmit={submitHandler}
        >
            {({ isSubmitting, isValid, errors }) => {
                return (
                    <Form className="form_users">
                        <div className="form_users__grid">
                        <div className="form_users__col">
                            <div className="form_users__row">
                                <Field
                                    id="username"
                                    label="Name *"
                                    name="username"
                                    autoComplete="off"
                                    as={TextInput}
                                    errors={errors.username}
                                />
                            </div>
                            <div className="form_users__row">
                                <Field
                                    id="password"
                                    label="Password *"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    passwordButton={toggleTypePassword}
                                    as={TextInput}
                                    autoComplete="off"
                                    placeholder=""
                                    disabled={disablePasswordField}
                                    errors={errors.password}
                                />
                                <button
                                    type="button"
                                    disabled={typeAction.type !== "update"}
                                    className="form_users__row_link"
                                    onClick={disablePasswordFieldButton}
                                >
                                    Change
                                </button>
                            </div>
                            <div className="form_users__row">
                                <div className="test">
                                    <Field
                                        id="repeat"
                                        label="Repeat *"
                                        name="repeat"
                                        type={showConfirmPassword ? "text" : "password"}
                                        passwordButton={toggleTypeConfirmPassword}
                                        autoComplete="off"
                                        placeholder=""
                                        as={TextInput}
                                        disabled={disablePasswordField}
                                        errors={errors.repeat}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form_users__col">
                            <div className="form_users__field">
                                <div className="form_users__field_label">
                                    Permissions
                                </div>
                                <SearchBarMultiSelect
                                    label="Permissions"
                                    options={permissionOptions}
                                    selectedIds={selectedPermissionIds}
                                    onChange={setSelectedPermissionIds}
                                />
                            </div>
                            <Select
                                key={`${roleOptions.length}-${userRoleId}`}
                                label="Role"
                                currentId={userRoleId}
                                options={roleOptions}
                                onChange={onChangeRoleHandler}
                            />
                        </div>

                        <div className="form_users__col">
                            <div className="form_users__field">
                                <div className="form_users__field_label">
                                    Products
                                </div>
                                <SearchBarMultiSelect
                                    label="Products"
                                    placeholder="Search Product"
                                    options={productOptions}
                                    selectedIds={selectedProductIds}
                                    onChange={setSelectedProductIds}
                                />
                            </div>
                        </div>

                        <div className="form_users__col">
                            <div className="form_users__field">
                                <div className="form_users__field_label">
                                    Projects
                                </div>
                                <SearchBarMultiSelect
                                    label="Projects"
                                    placeholder="Search Project"
                                    options={projectOptions}
                                    selectedIds={selectedProjectIds}
                                    onChange={setSelectedProjectIds}
                                />
                            </div>
                        </div>
                        </div>

                        <div className="form_users__footer">
                            <ActionButtons
                                className="form_users__button_submit"
                                actionButtons={[
                                    {
                                        type: "button",
                                        disabled: isSubmitting,
                                        name: "close",
                                        role: "secondary",
                                        onClick: () => {
                                            closeFormHandler(id);
                                        },
                                    },
                                    {
                                        type: "submit",
                                        disabled: isSubmitting || !isValid,
                                        name: "save",
                                        role: "primary",
                                    },
                                ]}
                            />
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default UsersSubRow;
